<section class="px-6 py-20 sm:px-10">
  <div class="mx-auto max-w-6xl">
    <p class="mb-6">
      <a
        [routerLink]="['/competitions', competitionId]"
        class="text-sm text-dusk hover:text-parchment hover:underline"
        >&larr; Back to competition</a
      >
    </p>

    @if (loading()) {
      <p class="text-dusk">Loading…</p>
    } @else if (errorMessage()) {
      <p class="rounded border border-wax/40 bg-wax/10 px-4 py-3 text-sm text-parchment">
        {{ errorMessage() }}
      </p>
    } @else if (competition(); as c) {
      <span class="mb-2 block text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-gold"
        >Competition</span
      >
      <h1 class="mb-8 font-display text-3xl font-semibold text-parchment">
        {{ c.name }} — Achievements
      </h1>

      <!-- The add form sits beside the list from lg up, so a long list doesn't
           push it out of sight. -->
      <div class="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
      <div class="rounded-md border border-hairline bg-panel p-6">
        <h2 class="text-sm font-semibold uppercase tracking-wide text-dusk">
          Configured achievements ({{ achievements().length }})
        </h2>
        @if (achievements().length === 0) {
          <p class="mt-3 text-sm text-dusk">No achievements configured yet.</p>
        } @else {
          <ul class="mt-3 flex flex-col gap-2">
            @for (a of achievements(); track a.id) {
              <li class="flex flex-col gap-3 rounded border border-hairline px-4 py-3">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <span class="text-sm font-semibold text-parchment">{{ displayName(a) }}</span>
                    @if (displayDescription(a); as description) {
                      <p class="mt-1 text-xs text-dusk">{{ description }}</p>
                    }
                  </div>
                  <div class="flex shrink-0 flex-col items-end gap-1">
                    <span class="text-xs font-semibold text-gold">
                      @if (a.awardType === 'POINTS') {
                        @if (a.pointsValue !== null) {
                          {{ a.pointsValue }} pts
                        } @else if (a.distributionMode === 'SPLIT') {
                          Split points
                        } @else {
                          Based on age
                        }
                      } @else {
                        {{ a.prizeDescription ?? 'Prize' }}
                      }
                    </span>
                    @if (isEditable(a)) {
                      <button
                        type="button"
                        (click)="editing.set(a)"
                        class="text-xs text-gold hover:underline"
                      >
                        Edit
                      </button>
                    }
                    @if (canManage() && !a.isFixed) {
                      @if (confirmingRemoveId() === a.id) {
                        <span class="flex items-center gap-2 text-xs">
                          <span class="text-parchment">Remove?</span>
                          <button
                            type="button"
                            (click)="confirmRemove(a)"
                            [disabled]="removing()"
                            class="font-semibold text-wax-bright hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {{ removing() ? 'Removing…' : 'Yes' }}
                          </button>
                          <button
                            type="button"
                            (click)="confirmingRemoveId.set(null)"
                            class="text-dusk hover:text-parchment"
                          >
                            Cancel
                          </button>
                        </span>
                      } @else {
                        <button
                          type="button"
                          (click)="confirmingRemoveId.set(a.id)"
                          class="text-xs text-wax-bright hover:underline"
                        >
                          Remove
                        </button>
                      }
                    }
                  </div>
                </div>

                @if (canManage() && isManuallyAwardable(a)) {
                  <div class="border-t border-hairline pt-3">
                    @if (holdersFor(a).length > 0) {
                      <p class="text-xs text-dusk">
                        Awarded to:
                        @for (h of holdersFor(a); track h.contestantId; let last = $last) {
                          <span class="text-parchment">{{ h.displayName }}</span
                          >{{ last ? '' : ', ' }}
                        }
                      </p>
                      @if (splitSuggestion(a); as split) {
                        <p class="mt-1 text-xs text-dusk">
                          Suggested split:
                          <span class="font-semibold text-gold"
                            >{{ split.unit === '$' ? '$' + split.amount : split.amount + ' pts' }} each</span
                          >
                          ({{ split.unit === '$' ? '$' + split.total : split.total + ' pts' }}
                          ÷ {{ split.holders }}, rounded up)
                        </p>
                      }
                    } @else {
                      <p class="text-xs text-dusk">Not yet awarded to anyone.</p>
                    }

                    @if (awardingAchievementId() === a.id) {
                      <div class="mt-2 flex flex-wrap items-end gap-2">
                        <label class="flex flex-col gap-1 text-xs">
                          <span class="text-dusk">Contestant</span>
                          <select
                            [formControl]="awardForm.controls.contestantId"
                            class="rounded border border-hairline bg-ink px-2 py-1.5 text-sm text-parchment focus:border-gold focus:outline-none"
                          >
                            <option [ngValue]="null">Select…</option>
                            @for (entry of leaderboard(); track entry.contestantId) {
                              <option [ngValue]="entry.contestantId">{{ entry.displayName }}</option>
                            }
                          </select>
                        </label>
                        @if (a.distributionMode === 'SPLIT') {
                          <label class="flex flex-col gap-1 text-xs">
                            <span class="text-dusk">Split amount</span>
                            <input
                              type="number"
                              [formControl]="awardForm.controls.splitAmount"
                              class="w-24 rounded border border-hairline bg-ink px-2 py-1.5 text-sm text-parchment focus:border-gold focus:outline-none"
                            />
                          </label>
                        }
                        <button
                          type="button"
                          (click)="submitAward(a)"
                          [disabled]="awarding()"
                          class="rounded-[3px] bg-gradient-to-b from-gold to-gold-dim px-3 py-1.5 text-xs font-bold text-ink transition hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {{ awarding() ? 'Awarding…' : 'Award' }}
                        </button>
                        <button
                          type="button"
                          (click)="closeAward()"
                          class="text-xs text-dusk hover:text-parchment"
                        >
                          Cancel
                        </button>
                      </div>
                      @if (awardError()) {
                        <p class="mt-2 rounded border border-wax/40 bg-wax/10 px-3 py-2 text-xs text-parchment">
                          {{ awardError() }}
                        </p>
                      }
                    } @else {
                      <button
                        type="button"
                        (click)="openAward(a)"
                        class="mt-2 text-xs text-gold hover:underline"
                      >
                        Award to a contestant…
                      </button>
                    }
                  </div>
                }
              </li>
            }
          </ul>
        }
        @if (removeError()) {
          <p class="mt-3 rounded border border-wax/40 bg-wax/10 px-3 py-2 text-xs text-parchment">
            {{ removeError() }}
          </p>
        }
      </div>

      @if (canManage()) {
        <div class="rounded-md border border-hairline bg-panel p-6">
          <h2 class="text-sm font-semibold uppercase tracking-wide text-dusk">
            Add an achievement
          </h2>

          <div class="mt-3 flex gap-2">
            <button
              type="button"
              (click)="mode.set('catalog')"
              class="rounded-[3px] border px-3 py-1.5 text-xs font-semibold transition"
              [class.border-gold]="mode() === 'catalog'"
              [class.text-gold]="mode() === 'catalog'"
              [class.border-hairline]="mode() !== 'catalog'"
              [class.text-dusk]="mode() !== 'catalog'"
            >
              Pick from catalog
            </button>
            <button
              type="button"
              (click)="mode.set('custom')"
              class="rounded-[3px] border px-3 py-1.5 text-xs font-semibold transition"
              [class.border-gold]="mode() === 'custom'"
              [class.text-gold]="mode() === 'custom'"
              [class.border-hairline]="mode() !== 'custom'"
              [class.text-dusk]="mode() !== 'custom'"
            >
              Create custom
            </button>
          </div>

          <form [formGroup]="form" (ngSubmit)="addAchievement()" class="mt-4 flex flex-col gap-3">
            @if (mode() === 'catalog') {
              @if (availableDefinitions().length === 0) {
                <p class="text-xs text-dusk">
                  Every catalog achievement is already attached to this competition.
                </p>
              } @else {
                <label class="flex flex-col gap-1 text-sm">
                  <span class="text-dusk">Achievement</span>
                  <select
                    formControlName="achievementDefinitionId"
                    class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
                  >
                    <option [ngValue]="null">Select an achievement…</option>
                    @for (d of availableDefinitions(); track d.id) {
                      <option [ngValue]="d.id">{{ d.name }}</option>
                    }
                  </select>
                </label>
                @if (selectedDefinition(); as selected) {
                  <p class="rounded border border-hairline bg-ink px-3 py-2 text-xs text-dusk">
                    {{ selected.description }}
                  </p>
                }
              }
            } @else {
              <label class="flex flex-col gap-1 text-sm">
                <span class="text-dusk">Name</span>
                <input
                  formControlName="name"
                  type="text"
                  class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
                />
              </label>
              <label class="flex flex-col gap-1 text-sm">
                <span class="text-dusk">Description</span>
                <textarea
                  formControlName="description"
                  rows="2"
                  class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
                ></textarea>
              </label>
            }

            <div class="grid grid-cols-2 gap-3">
              <label class="flex flex-col gap-1 text-sm">
                <span class="text-dusk">Award type</span>
                <select
                  formControlName="awardType"
                  class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
                >
                  <option value="POINTS">Points</option>
                  <option value="PRIZE">Prize</option>
                </select>
              </label>
              <label class="flex flex-col gap-1 text-sm">
                <span class="text-dusk">Distribution</span>
                <select
                  formControlName="distributionMode"
                  class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
                >
                  <option value="DUPLICATED">Duplicated</option>
                  <option value="SPLIT">Split</option>
                </select>
              </label>
            </div>

            @if (awardType() === 'POINTS') {
              <label class="flex flex-col gap-1 text-sm">
                <span class="text-dusk">Points</span>
                <input
                  formControlName="pointsValue"
                  type="number"
                  class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
                />
              </label>
            } @else {
              <label class="flex flex-col gap-1 text-sm">
                <span class="text-dusk">Prize description</span>
                <input
                  formControlName="prizeDescription"
                  type="text"
                  class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
                />
              </label>
              <label class="flex flex-col gap-1 text-sm">
                <span class="text-dusk">Dollar value (optional)</span>
                <input
                  formControlName="prizeDollarValue"
                  type="number"
                  class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
                />
              </label>
            }

            @if (addError()) {
              <p class="rounded border border-wax/40 bg-wax/10 px-3 py-2 text-xs text-parchment">
                {{ addError() }}
              </p>
            }

            <div>
              <button
                type="submit"
                [disabled]="adding()"
                class="inline-flex items-center justify-center rounded-[3px] bg-gradient-to-b from-gold to-gold-dim px-5 py-2 text-sm font-bold text-ink transition hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-60"
              >
                {{ adding() ? 'Adding…' : 'Add achievement' }}
              </button>
            </div>
          </form>
        </div>
      } @else if (isOwner()) {
        <p class="text-xs text-dusk">
          Upgrade to a paid plan to pick more achievements or create your own.
        </p>
      }
      </div>
    }
  </div>

@if (editing(); as achievement) {
  <app-achievement-edit-modal
    [achievement]="achievement"
    (saved)="onEdited($event)"
    (closed)="editing.set(null)"
  />
}
</section>
